import React, { memo } from 'react';
import { FiCheck } from 'react-icons/fi';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import moment from 'moment';

import Avatar from '~components/avatar';
import Loader from '~components/loader';

export const Message = memo(({ body, createdAt, sender = {}, isReversed, isDelivered, isSeen, isSending }) => {
  const intl = useIntl();

  const createdMoment = moment.unix(createdAt);
  const isToday = parseInt(createdMoment.unix(), 10) > parseInt(moment().startOf('day').unix(), 10);

  const messageLabel = intl.formatMessage(
    { id: 'components.chat.message-label', defaultMessage: 'Message from {sender}' },
    { sender: sender.firstName || 'Unknown' }
  );

  return (
    <div
      className={cx('c-chat__message c-message', {
        'c-message--interlocutor': isReversed,
        'c-message--moderator': sender.participationMode === 'moderator',
      })}
      role="article"
      aria-label={messageLabel}
    >
      <div className="c-message__content">
        <div className="c-message__context">
          <div className="c-message__date">
            {!isToday
              ? `${intl.formatDate(createdMoment.toDate(), { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : intl.formatTime(createdMoment.toDate(), { hour: 'numeric', minute: 'numeric' })}
          </div>
          <div
            className={`c-message__status ${isSeen ? 'c-message__status--seen' : ''}`}
          >
            {isSending && (
              <>
                <Loader />
                <span className="sr-only">
                  {intl.formatMessage({ id: 'components.chat.status.sending', defaultMessage: 'Sending' })}
                </span>
              </>
            )}
            {isDelivered && !isSeen && (
              <>
                <FiCheck aria-hidden="true" />
                <span className="sr-only">
                  {intl.formatMessage({ id: 'components.chat.status.delivered', defaultMessage: 'Delivered' })}
                </span>
              </>
            )}
            {isSeen && (
              <>
                <FiCheck aria-hidden="true" />
                <span className="sr-only">
                  {intl.formatMessage({ id: 'components.chat.status.seen', defaultMessage: 'Seen' })}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="c-message__text">{body}</div>
      </div>
      <div className="c-message__person c-photo">
        <Avatar
          profilePicture={
            sender.participationMode === 'moderator' && !sender.profilePicture
              ? '/images/employee-avatar.svg'
              : sender.profilePicture
          }
          firstName={sender.firstName}
        />
      </div>
    </div>
  );
});

export const MessageTyping = ({ sender = {} }) => {
  return (
    <div className={cx('c-chat__message c-message c-message--interlocutor c-message--typing')}>
      {/* Screen reader announcement for typing indicator */}
      <span className="sr-only" role="status" aria-live="polite">
        {sender.firstName || 'Someone'} is typing
      </span>
      <div className="c-message__content">
        <div className="c-message__context">
          <div className="c-message__date" />
          <div className="c-message__status" />
        </div>
        <div className="c-message__text" aria-hidden="true">
          <div className="c-message__ellipsis-wrapper">
            <div className="c-message__ellipsis">...</div>
          </div>
        </div>
      </div>
      <div className="c-message__person c-photo">
        <Avatar
          profilePicture={
            sender.participationMode === 'moderator' && !sender.profilePicture
              ? '/images/employee-avatar.svg'
              : sender.profilePicture
          }
          firstName={sender.firstName}
        />
      </div>
    </div>
  );
};
